import { useEffect, useRef, useState, useCallback } from 'react'
import type { Map as MapLibreMap } from 'maplibre-gl'
import { TerraDraw, TerraDrawPolygonMode, TerraDrawSelectMode } from 'terra-draw'
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter'
import type * as GeoJSON from 'geojson'
import simplify from '@turf/simplify'
import { getTurfColor } from '../lib/mapConfig'
import { computeContactsInPolygon } from '../utils/geoUtils'
import type { ContactLocation } from '../types/contactList'
import type { ManualRecutPolygon } from '../types/campaign'
import type { OverlayMode } from '../types/workplaceOverlay'
import { terraDrawTouchTuning } from './terraDrawTouchTuning'

// =====================================================================
// HELPERS
// =====================================================================

/** Simplify a polygon geometry to reduce dense vertex clusters from auto-cutting.
 *  Uses @turf/simplify. Tolerance ~5m in degrees. Might need further tweaking.*/
function simplifyGeometry<T extends GeoJSON.Polygon | GeoJSON.MultiPolygon>(
  geometry: T,
  tolerance = 0.00005
): T {
  const feature: GeoJSON.Feature<T> = { type: 'Feature', geometry, properties: {} }
  const simplified = simplify(feature, { tolerance, highQuality: true })
  return simplified.geometry as T
}

// =====================================================================
// TYPES
// =====================================================================

export interface DrawnTurf {
  id: string // terra-draw feature ID
  index: number // 1-based turf number
  polygon: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>
  color: string
  contactIds: string[]
  householdCount: number
  label: string
}

export interface LoadablePolygon {
  id: string
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon
  label: string
  color: string
  index: number
  contactIds?: string[]
  householdCount?: number
}

export interface UseTerraDrawOptions {
  mapRef: React.MutableRefObject<MapLibreMap | null>
  contacts: ContactLocation[]
  onTurfsChange: (turfs: DrawnTurf[]) => void
  /** When true, only SelectMode is enabled (edit existing polygons, no drawing new ones) */
  editOnly?: boolean
  /** Label prefix for default turf names (e.g. "Downtown" → "Downtown-1") */
  turfMapLabel?: string
  /** Controls which contact location (home/work/hybrid) is used for point-in-polygon checks */
  overlayMode?: OverlayMode
  /** Enlarge terra-draw hit targets for touch input (mobile). Default false. */
  touch?: boolean
}

export interface UseTerraDrawReturn {
  drawnTurfs: DrawnTurf[]
  isDrawingMode: boolean
  isMapReady: boolean
  isDirty: boolean
  toggleDrawingMode: () => void
  startDrawing: () => void
  stopDrawing: () => void
  loadExistingPolygons: (polygons: LoadablePolygon[]) => void
  deleteTurf: (turfId: string) => void
  updateTurfLabel: (turfId: string, label: string) => void
  flyToTurf: (turfId: string) => void
  revertToOriginal: () => void
  getEditedPolygons: (assignmentMap?: Map<string, string | null>) => ManualRecutPolygon[]
  handleMapLoad: (map: MapLibreMap) => void
}

// =====================================================================
// HOOK
// =====================================================================

export function useTerraDraw({
  mapRef,
  contacts,
  onTurfsChange,
  editOnly = false,
  turfMapLabel,
  overlayMode = 'home',
  touch = false,
}: UseTerraDrawOptions): UseTerraDrawReturn {
  const drawRef = useRef<TerraDraw | null>(null)
  const [drawnTurfs, setDrawnTurfs] = useState<DrawnTurf[]>([])
  const [isDrawingMode, setIsDrawingMode] = useState(false)
  const [isMapReady, setIsMapReady] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [mapLoadCount, setMapLoadCount] = useState(0)
  const nextIndexRef = useRef(1)
  const turfMapLabelRef = useRef(turfMapLabel)
  turfMapLabelRef.current = turfMapLabel

  // Snapshot of originally-loaded polygons for revert
  const originalPolygonsRef = useRef<LoadablePolygon[]>([])

  // Refs for latest values accessed inside event handlers
  const contactsRef = useRef(contacts)
  contactsRef.current = contacts
  const onTurfsChangeRef = useRef(onTurfsChange)
  onTurfsChangeRef.current = onTurfsChange
  const isDrawingModeRef = useRef(isDrawingMode)
  isDrawingModeRef.current = isDrawingMode
  const drawnTurfsRef = useRef(drawnTurfs)
  drawnTurfsRef.current = drawnTurfs
  const overlayModeRef = useRef(overlayMode)
  overlayModeRef.current = overlayMode

  // Handle map load — clean up any previous terra-draw and trigger re-initialization
  const handleMapLoad = useCallback((map: MapLibreMap) => {
    // Clean up previous terra-draw instance if map was recreated (e.g. modal reopen)
    if (drawRef.current) {
      try { drawRef.current.stop() } catch { /* map may be destroyed */ }
      drawRef.current = null
    }
    mapRef.current = map
    setIsMapReady(false) // Reset — will be set to true by init effect
    setMapLoadCount(c => c + 1) // Trigger init effect re-run
  }, [mapRef])

  // Initialize terra-draw when a new map loads
  useEffect(() => {
    if (mapLoadCount === 0 || !mapRef.current || drawRef.current) return

    const map = mapRef.current

    const tune = terraDrawTouchTuning(touch)
    const pd = tune.pointerDistance !== undefined ? { pointerDistance: tune.pointerDistance } : {}
    const selectStyles = tune.selectionPointWidth !== undefined
      ? { styles: { selectionPointWidth: tune.selectionPointWidth, selectionPointOutlineWidth: 2 } }
      : {}

    const selectMode = () =>
      new TerraDrawSelectMode({
        ...pd,
        ...selectStyles,
        flags: {
          polygon: {
            feature: {
              draggable: false,
              coordinates: {
                midpoints: false,
                draggable: true,
                deletable: true,
              },
            },
          },
        },
      })

    const modes = editOnly
      ? [selectMode()]
      : [
          new TerraDrawPolygonMode({
            ...pd,
            styles: {
              fillColor: '#3182ce',
              fillOpacity: 0,
              outlineColor: '#3182ce',
              outlineWidth: 1,
              closingPointColor: '#3182ce',
              closingPointOutlineColor: '#ffffff',
              closingPointWidth: tune.closingPointWidth,
            },
          }),
          selectMode(),
        ]

    const draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map }),
      modes,
    })

    // Listen for polygon completion (only relevant if !editOnly)
    if (!editOnly) {
      draw.on('finish', (id: string | number) => {
        if (!isDrawingModeRef.current) return

        const snapshot = draw.getSnapshot()
        const feature = snapshot.find(f => f.id === id)

        if (feature && feature.geometry.type === 'Polygon') {
          const simplifiedGeom = simplifyGeometry(feature.geometry as GeoJSON.Polygon)
          const polygon: GeoJSON.Feature<GeoJSON.Polygon> = {
            type: 'Feature',
            geometry: simplifiedGeom,
            properties: feature.properties || {},
          }

          // Update terra-draw with simplified geometry
          draw.updateFeatureGeometry(id, simplifiedGeom)

          const { contactIds, householdCount } = computeContactsInPolygon(
            contactsRef.current,
            polygon,
            overlayModeRef.current
          )

          const turfIndex = nextIndexRef.current
          const newTurf: DrawnTurf = {
            id: String(id),
            index: turfIndex,
            polygon,
            color: getTurfColor(turfIndex),
            contactIds,
            householdCount,
            label: `Turf ${turfIndex}`,
          }

          setDrawnTurfs(prev => {
            const updated = [...prev, newTurf]
            onTurfsChangeRef.current(updated)
            return updated
          })
          setIsDirty(true)

          nextIndexRef.current += 1

          draw.setMode('select')
          setIsDrawingMode(false)
        }
      })
    }

    // Listen for polygon edits (vertex drag)
    draw.on('change', (ids: (string | number)[], type: string) => {
      if (type !== 'update') return

      const snapshot = draw.getSnapshot()
      let changed = false

      setDrawnTurfs(prev => {
        const updated = prev.map(turf => {
          const changedId = ids.find(id => String(id) === turf.id)
          if (changedId === undefined) return turf

          const feature = snapshot.find(f => String(f.id) === turf.id)
          if (!feature || feature.geometry.type !== 'Polygon') return turf

          const polygon: GeoJSON.Feature<GeoJSON.Polygon> = {
            type: 'Feature',
            geometry: feature.geometry as GeoJSON.Polygon,
            properties: feature.properties || {},
          }

          const { contactIds, householdCount } = computeContactsInPolygon(
            contactsRef.current,
            polygon,
            overlayModeRef.current
          )

          changed = true
          return { ...turf, polygon, contactIds, householdCount }
        })

        if (changed) {
          onTurfsChangeRef.current(updated)
          setIsDirty(true)
        }
        return changed ? updated : prev
      })
    })

    // Start in select mode
    draw.start()
    draw.setMode('select')
    drawRef.current = draw
    setIsMapReady(true)

    // Right-click to cancel drawing
    if (!editOnly) {
      const handleContextMenu = (e: MouseEvent) => {
        e.preventDefault()
        if (isDrawingModeRef.current && drawRef.current) {
          drawRef.current.setMode('select')
          setIsDrawingMode(false)
        }
      }
      map.getCanvas().addEventListener('contextmenu', handleContextMenu)

      return () => {
        try {
          map.getCanvas().removeEventListener('contextmenu', handleContextMenu)
        } catch { /* map may be destroyed */ }
      }
    }
  }, [mapLoadCount, editOnly, mapRef, touch])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (drawRef.current) {
        try { drawRef.current.stop() } catch { /* map may already be destroyed */ }
        drawRef.current = null
      }
    }
  }, [])

  // Recalculate contacts in existing turfs when contacts or overlayMode changes
  // (e.g. employment data loads, user switches overlay mode)
  const prevContactsLenRef = useRef(0)
  const prevOverlayRef = useRef(overlayMode)
  useEffect(() => {
    const contactsChanged = contacts.length !== prevContactsLenRef.current
    const overlayChanged = overlayMode !== prevOverlayRef.current
    prevContactsLenRef.current = contacts.length
    prevOverlayRef.current = overlayMode

    if (!contactsChanged && !overlayChanged) return
    if (drawnTurfs.length === 0) return

    setDrawnTurfs(prev => {
      const updated = prev.map(turf => {
        if (turf.polygon.geometry.type !== 'Polygon') return turf
        const { contactIds, householdCount } = computeContactsInPolygon(
          contacts,
          turf.polygon as GeoJSON.Feature<GeoJSON.Polygon>,
          overlayMode
        )
        return { ...turf, contactIds, householdCount }
      })
      onTurfsChangeRef.current(updated)
      return updated
    })
  }, [contacts, overlayMode, drawnTurfs.length])

  // Render colored turf boundaries as GeoJSON layers
  useEffect(() => {
    if (!mapRef.current || !isMapReady) return

    const map = mapRef.current
    const sourceId = 'turf-boundaries-multi'
    const fillLayerId = 'turf-boundaries-fill-multi'
    const lineLayerId = 'turf-boundaries-line-multi'

    // Remove existing layers/sources — guard against destroyed map
    try {
      if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId)
      if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId)
      if (map.getSource(sourceId)) map.removeSource(sourceId)
    } catch {
      return // Map is destroyed, nothing to do
    }

    if (drawnTurfs.length === 0) return

    const featureCollection: GeoJSON.FeatureCollection<GeoJSON.Polygon | GeoJSON.MultiPolygon> = {
      type: 'FeatureCollection',
      features: drawnTurfs.map(turf => ({
        ...turf.polygon,
        properties: {
          ...turf.polygon.properties,
          turfIndex: turf.index,
          color: turf.color,
        },
      })),
    }

    map.addSource(sourceId, { type: 'geojson', data: featureCollection })

    map.addLayer({
      id: fillLayerId,
      type: 'fill',
      source: sourceId,
      paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.2,
      },
    })

    map.addLayer({
      id: lineLayerId,
      type: 'line',
      source: sourceId,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2,
      },
    })

    return () => {
      try {
        if (map.getLayer(fillLayerId)) map.removeLayer(fillLayerId)
        if (map.getLayer(lineLayerId)) map.removeLayer(lineLayerId)
        if (map.getSource(sourceId)) map.removeSource(sourceId)
      } catch {
        // Map may be destroyed during modal close — safe to ignore
      }
    }
  }, [drawnTurfs, mapRef, isMapReady])

  // =========================================================================
  // Methods
  // =========================================================================

  const startDrawing = useCallback(() => {
    if (!drawRef.current || editOnly) return
    drawRef.current.setMode('polygon')
    setIsDrawingMode(true)
  }, [editOnly])

  const stopDrawing = useCallback(() => {
    if (!drawRef.current) return
    drawRef.current.setMode('select')
    setIsDrawingMode(false)
  }, [])

  const toggleDrawingMode = useCallback(() => {
    if (isDrawingMode) stopDrawing()
    else startDrawing()
  }, [isDrawingMode, startDrawing, stopDrawing])

  const deleteTurf = useCallback(
    (turfId: string) => {
      if (drawRef.current) {
        try {
          drawRef.current.removeFeatures([turfId])
        } catch {
          // Feature may already be removed
        }
      }
      setDrawnTurfs(prev => {
        const updated = prev.filter(t => t.id !== turfId)
        onTurfsChange(updated)
        return updated
      })
      setIsDirty(true)
    },
    [onTurfsChange]
  )

  const updateTurfLabel = useCallback(
    (turfId: string, newLabel: string) => {
      setDrawnTurfs(prev => {
        const updated = prev.map(t => (t.id === turfId ? { ...t, label: newLabel } : t))
        onTurfsChange(updated)
        return updated
      })
    },
    [onTurfsChange]
  )

  const flyToTurf = useCallback(
    (turfId: string) => {
      const turf = drawnTurfsRef.current.find(t => t.id === turfId)
      if (turf && mapRef.current) {
        const geometry = turf.polygon.geometry
        let outerRing: GeoJSON.Position[]

        if (geometry.type === 'Polygon') {
          outerRing = geometry.coordinates[0]
        } else {
          outerRing = geometry.coordinates[0][0]
        }

        const bounds = outerRing.reduce(
          (b, coord) => [
            [Math.min(b[0][0], coord[0] as number), Math.min(b[0][1], coord[1] as number)],
            [Math.max(b[1][0], coord[0] as number), Math.max(b[1][1], coord[1] as number)],
          ],
          [
            [outerRing[0][0] as number, outerRing[0][1] as number],
            [outerRing[0][0] as number, outerRing[0][1] as number],
          ]
        )
        mapRef.current.fitBounds(bounds as [[number, number], [number, number]], {
          padding: 50,
          duration: 800,
        })
      }
    },
    [mapRef]
  )

  /**
   * Load existing polygons into terra-draw for editing.
   * Stores a snapshot for revert. Replaces any current features.
   */
  const loadExistingPolygons = useCallback(
    (polygons: LoadablePolygon[]) => {
      if (!drawRef.current) return

      const draw = drawRef.current

      // Ensure we're in select mode before clearing
      draw.setMode('select')
      setIsDrawingMode(false)

      // Clear ALL existing features from terra-draw
      try {
        const snapshot = draw.getSnapshot()
        const allFeatureIds = snapshot
          .map(f => f.id)
          .filter((id): id is string | number => id !== undefined)
        if (allFeatureIds.length > 0) {
          draw.removeFeatures(allFeatureIds)
        }
      } catch {
        // Ignore cleanup errors
      }

      // Store snapshot for revert
      originalPolygonsRef.current = polygons

      // Reset index counter
      nextIndexRef.current = 1

      const newTurfs: DrawnTurf[] = []

      polygons.forEach(p => {
        try {
          if (!p.geometry) return

          // Simplify dense auto-cut polygons to make vertex editing manageable
          const simplified = simplifyGeometry(p.geometry)

          const feature: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> = {
            type: 'Feature',
            geometry: simplified,
            properties: { mode: 'polygon' },
          }

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const results = draw.addFeatures([feature as any])
          const result = results[0]
          const featureId = result?.id

          if (!featureId) return

          // Compute contacts if not provided
          let contactIds = p.contactIds ?? []
          let householdCount = p.householdCount ?? 0
          if (!p.contactIds && simplified.type === 'Polygon') {
            const computed = computeContactsInPolygon(contactsRef.current, {
              type: 'Feature',
              geometry: simplified,
              properties: {},
            }, overlayModeRef.current)
            contactIds = computed.contactIds
            householdCount = computed.householdCount
          }

          newTurfs.push({
            id: String(featureId),
            index: p.index,
            polygon: { type: 'Feature', geometry: simplified, properties: {} },
            color: p.color,
            contactIds,
            householdCount,
            label: p.label,
          })

          if (p.index >= nextIndexRef.current) {
            nextIndexRef.current = p.index + 1
          }
        } catch (err) {
          console.error('[useTerraDraw] Failed to load polygon:', p.label, err)
        }
      })

      setDrawnTurfs(newTurfs)
      onTurfsChange(newTurfs)
      setIsDirty(false)
    },
    [onTurfsChange]
  )

  /** Revert all polygons to the state when loadExistingPolygons was last called. */
  const revertToOriginal = useCallback(() => {
    if (originalPolygonsRef.current.length > 0) {
      loadExistingPolygons(originalPolygonsRef.current)
    }
    setIsDirty(false)
  }, [loadExistingPolygons])

  /** Export current polygons as ManualRecutPolygon[] for saving to backend. */
  const getEditedPolygons = useCallback(
    (assignmentMap?: Map<string, string | null>): ManualRecutPolygon[] => {
      return drawnTurfsRef.current.map(turf => ({
        turfIndex: turf.index,
        label: turf.label,
        boundaryGeoJson: JSON.stringify(turf.polygon.geometry),
        assignedCanvasserId: assignmentMap?.get(String(turf.index)) ?? assignmentMap?.get(turf.id) ?? null,
      }))
    },
    []
  )

  return {
    drawnTurfs,
    isDrawingMode,
    isMapReady,
    isDirty,
    toggleDrawingMode,
    startDrawing,
    stopDrawing,
    loadExistingPolygons,
    deleteTurf,
    updateTurfLabel,
    flyToTurf,
    revertToOriginal,
    getEditedPolygons,
    handleMapLoad,
  }
}
